import { useEffect, useState } from 'react';
import { Typography, Form, Row, Col, Spin, Button, notification } from 'antd';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { AddIcCallOutlined, CallOutlined, Message } from '@mui/icons-material';
import { getCampaignById } from '@/api/novi/campaigns';
import { updateCampaignName } from '@/api/novi/campaigns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSelectedAgentId } from '@/store/account/selector';
import { updateSelectedAgent } from '@/store/account/slice';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';

const { Title } = Typography;

interface GeneralFormData {
	name: string;
	campaignGoal: string;
	communicationType: string;
}

const defaultFormData: GeneralFormData = {
	name: '',
	campaignGoal: '',
	communicationType: '',
};

const GeneralSettings = () => {
	const [formData, setFormData] = useState<GeneralFormData>(defaultFormData);
	const [originalData, setOriginalData] = useState<GeneralFormData>(defaultFormData);
	const [hasChanges, setHasChanges] = useState(false);

	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const queryClient = useQueryClient();
	const dispatch = useAppDispatch();

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	const handleRadioChange = (name: keyof GeneralFormData, value: string) => {
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	useEffect(() => {
		const nameChanged = formData.name !== originalData.name;
		const goalChanged = formData.campaignGoal !== originalData.campaignGoal;
		setHasChanges(nameChanged || goalChanged);
	}, [formData, originalData]);

	const { data: campaignDetails, isLoading } = useQuery({
		queryKey: ['campaign-details', selectedAgentId],
		queryFn: () => {
			if (!selectedAgentId) throw new Error('No agent selected');
			return getCampaignById(selectedAgentId);
		},
		enabled: !!selectedAgentId,
		retry: 2,
		staleTime: 0,
		refetchOnMount: true,
	});

	const updateMutation = useMutation({
		mutationFn: (payload: {
			campaign_id: string;
			name: string;
			campaignGoal: string;
			clientType: 'Reseller' | 'Client';
		}) => updateCampaignName(payload),
		onSuccess: () => {
			notification.success({
				message: 'Success',
				description: 'Campaign updated successfully',
			});

			dispatch(
				updateSelectedAgent({
					user: {
						name: formData.name,
						campaignGoal: formData.campaignGoal,
					},
				}),
			);
			setOriginalData({ ...formData });
			setHasChanges(false);
			queryClient.invalidateQueries({ queryKey: ['campaign-details', selectedAgentId] });
		},
		onError: () => {
			notification.error({
				message: 'Error',
				description: 'Failed to update campaign',
			});
		},
	});

	const handleSave = () => {
		if (!selectedAgentId) {
			notification.error({
				message: 'Error',
				description: 'No campaign selected',
			});
			return;
		}

		updateMutation.mutate({
			campaign_id: selectedAgentId,
			name: formData.name,
			campaignGoal: formData.campaignGoal,
			clientType: 'Client',
		});
	};

	const handleCancel = () => {
		setFormData({ ...originalData });
		setHasChanges(false);
	};

	useEffect(() => {
		if (campaignDetails?.campaign) {
			const mappedData = {
				name: campaignDetails.campaign?.name || '',
				campaignGoal: campaignDetails.campaign?.campaignGoal || '',
				communicationType: campaignDetails.campaign?.communicationType || '',
			};

			setFormData(mappedData);
			setOriginalData(mappedData);
		}
	}, [campaignDetails]);

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-64">
				<Spin size="large" tip="Loading schedule settings..." />
			</div>
		);
	}

	return (
		<div className="bg-white p-6 rounded-lg">
			<div className="flex justify-between items-center mb-4">
				<Title level={5}>General Settings</Title>
				{hasChanges && (
					<div className="space-x-2">
						<Button onClick={handleCancel} disabled={updateMutation.isPending}>
							Cancel
						</Button>
						<Button type="primary" onClick={handleSave} loading={updateMutation.isPending}>
							Save Changes
						</Button>
					</div>
				)}
			</div>

			<Form>
				<div className="mt-4">
					<label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name</label>
					<input
						type="text"
						name="name"
						value={formData.name}
						onChange={handleInputChange}
						className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
						placeholder="Enter campaign name"
					/>
				</div>

				<div className="mt-4">
					<label className="block text-sm font-medium text-gray-700 mb-2">Campaign Goal</label>
					<input
						type="text"
						name="campaignGoal"
						value={formData.campaignGoal}
						onChange={handleInputChange}
						className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
						placeholder="Enter campaign goal"
					/>
				</div>

				<div className="my-6">
					<label className="block text-sm font-medium text-gray-700 mb-2">Schedule Type</label>
					<div className="space-y-3">
						<Row gutter={[6, 6]}>
							{[
								{
									type: 'outbound-calls',
									icon: <CallOutlined className="text-xl" />,
									label: 'Outbound Calling',
									desc: 'Our AI automatically calls leads and engages them in natural conversations.',
								},
								{
									type: 'outbound-texting',
									icon: <Message className="text-xl" />,
									label: 'Outbound Texting',
									desc: 'Our AI sends personalized text messages and responds to the replies.',
								},
								{
									type: 'outbound-calls-texting',
									icon: <AddIcCallOutlined className="text-xl" />,
									label: 'Calling + Texting',
									desc: 'Combined approach with calls and follow-up texts for maximum engagements.',
								},
							].map(({ type, icon, label, desc }) => (
								<Col lg={8} md={24} key={type}>
									<label
										className={`block p-4 border rounded-lg cursor-pointer ${formData.communicationType === type ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
									>
										<div className="flex items-center">
											<input
												type="radio"
												name="scheduleType"
												value={type}
												checked={formData.communicationType === type}
												onChange={() => handleRadioChange('communicationType', type)}
												style={{ display: 'none' }}
											/>
											<div
												className={`w-6 h-6 mr-3 ${formData.communicationType === type ? 'text-blue-500' : 'text-gray-500'}`}
											>
												{icon}
											</div>
											<div>
												<div className="font-medium">{label}</div>
												<div className="text-sm text-gray-500">{desc}</div>
											</div>
										</div>
									</label>
								</Col>
							))}
						</Row>
					</div>
				</div>
			</Form>
		</div>
	);
};

export default GeneralSettings;
